import { useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { format, addDays } from "date-fns";
import {
  ArrowLeftRight,
  Calendar,
  Check,
  ChevronDown,
  Minus,
  Plus,
  Search,
} from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { publicApi } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useDateLocale } from "@/lib/dateLocale";
import {
  cityLabel,
  cityOptionKeywords,
  cityOptionValue,
} from "@/lib/city-option";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";

type TripType = "one-way" | "round-trip";

interface City {
  id: number;
  name: string;
  state: string | null;
}

interface FormState {
  from: string;
  to: string;
  date: Date | undefined;
  returnDate: Date | undefined;
  passengers: number;
  tripType: TripType;
}

/**
 * Ticket-counter styled booking widget.
 * Hairline dividers replace shadcn borders; display type on the destinations;
 * tabular numerals on dates and counts. Brand color drives the CTA.
 */
export function BookingWidget() {
  const { t } = useTranslation();
  const { slug } = useTenant();
  const [, setLocation] = useLocation();

  const [formState, setFormState] = useState<FormState>({
    from: "",
    to: "",
    date: undefined,
    returnDate: undefined,
    passengers: 1,
    tripType: "one-way",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: cities = [], isLoading: citiesLoading } = useQuery({
    queryKey: ["cities", slug],
    queryFn: async () => {
      if (!slug) return [];
      return publicApi.get<City[]>(`/api/public/${slug}/cities`);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = addDays(today, 90);

  const updateField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const swapCities = () => {
    setFormState((prev) => ({ ...prev, from: prev.to, to: prev.from }));
  };

  const validateForm = (): boolean => {
    const next: Record<string, string> = {};
    if (!formState.from) next.from = t("search.validation.originRequired");
    if (!formState.to) next.to = t("search.validation.destinationRequired");
    if (!formState.date) next.date = t("search.validation.dateRequired");
    if (formState.tripType === "round-trip") {
      if (!formState.returnDate) {
        next.returnDate = t("search.validation.returnDateRequired");
      } else if (formState.date && formState.returnDate < formState.date) {
        next.returnDate = t("search.validation.returnDateAfterDeparture");
      }
    }
    if (formState.from && formState.to && formState.from === formState.to) {
      next.to = t("search.validation.sameOriginDestination");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    trackEvent("search_initiated", {
      originCityId: parseInt(formState.from, 10),
      destinationCityId: parseInt(formState.to, 10),
      tripType: formState.tripType,
      passengerCount: formState.passengers,
    });

    const params = new URLSearchParams({
      date: format(formState.date!, "yyyy-MM-dd"),
      from: formState.from,
      to: formState.to,
      passengers: formState.passengers.toString(),
      tripType: formState.tripType,
    });
    if (formState.tripType === "round-trip" && formState.returnDate) {
      params.set("returnDate", format(formState.returnDate, "yyyy-MM-dd"));
    }
    setLocation(`/trips?${params.toString()}`);
  };

  const availableFromCities = formState.to
    ? cities.filter((c) => c.id.toString() !== formState.to)
    : cities;
  const availableToCities = formState.from
    ? cities.filter((c) => c.id.toString() !== formState.from)
    : cities;

  return (
    <div className="w-full bg-white shadow-ticket border hairline-strong">
      {/* Header: eyebrow + trip type pills */}
      <div className="flex items-center justify-between gap-4 px-5 md:px-7 pt-5 pb-4 border-b hairline-strong">
        <span className="text-eyebrow uppercase text-ink-subtle">
          {t("search.bookYourTicket", "Book your ticket")}
        </span>
        <div
          className="inline-flex text-xs"
          role="radiogroup"
          aria-label={t("search.tripType")}
        >
          <TripTypePill
            active={formState.tripType === "one-way"}
            onClick={() => updateField("tripType", "one-way")}
            label={t("search.oneWay")}
            testId="trip-type-one-way"
          />
          <TripTypePill
            active={formState.tripType === "round-trip"}
            onClick={() => updateField("tripType", "round-trip")}
            label={t("search.roundTrip")}
            testId="trip-type-round-trip"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Field grid — stacks on mobile, row on lg */}
        <div
          className={cn(
            "grid grid-cols-1 lg:divide-x divide-y lg:divide-y-0 divide-hairline",
            formState.tripType === "round-trip"
              ? "lg:grid-cols-[minmax(0,1.3fr)_3.5rem_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
              : "lg:grid-cols-[minmax(0,1.5fr)_3.5rem_minmax(0,1.5fr)_minmax(0,1.2fr)_minmax(0,1fr)]",
          )}
        >
          {/* From */}
          <Field
            label={t("search.from")}
            error={errors.from}
          >
            <CityField
              value={formState.from}
              onChange={(v) => updateField("from", v)}
              placeholder={
                citiesLoading ? t("common.loading", "Loading...") : t("search.selectOrigin")
              }
              cities={availableFromCities}
              disabled={citiesLoading}
              invalid={!!errors.from}
              testId="city-select-origin"
            />
          </Field>

          {/* Swap */}
          <div className="hidden lg:flex items-center justify-center px-2">
            <button
              type="button"
              onClick={swapCities}
              disabled={!formState.from && !formState.to}
              className="h-9 w-9 flex items-center justify-center border hairline-strong text-ink-muted hover:text-ink hover:border-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible-ring"
              aria-label={t("search.swap", "Swap origin and destination")}
            >
              <ArrowLeftRight className="h-4 w-4" />
            </button>
          </div>
          {/* Mobile swap bar */}
          <div className="lg:hidden flex items-center justify-center -my-px py-2 bg-stone-50">
            <button
              type="button"
              onClick={swapCities}
              disabled={!formState.from && !formState.to}
              className="h-8 w-8 flex items-center justify-center border hairline-strong bg-white text-ink-muted hover:text-ink disabled:opacity-40 focus-visible-ring"
              aria-label={t("search.swap", "Swap origin and destination")}
            >
              <ArrowLeftRight className="h-3.5 w-3.5 rotate-90" />
            </button>
          </div>

          {/* To */}
          <Field
            label={t("search.to")}
            error={errors.to}
          >
            <CityField
              value={formState.to}
              onChange={(v) => updateField("to", v)}
              placeholder={
                citiesLoading ? t("common.loading", "Loading...") : t("search.selectDestination")
              }
              cities={availableToCities}
              disabled={citiesLoading}
              invalid={!!errors.to}
              testId="city-select-destination"
            />
          </Field>

          {/* Departure date */}
          <Field
            label={t("search.departureDate")}
            error={errors.date}
          >
            <DateField
              value={formState.date}
              onChange={(d) => updateField("date", d)}
              minDate={today}
              maxDate={maxDate}
              placeholder={t("search.selectDepartureDate")}
              invalid={!!errors.date}
              testId="date-picker-departure"
            />
          </Field>

          {/* Return date (round-trip) */}
          {formState.tripType === "round-trip" ? (
            <Field
              label={t("search.returnDate")}
              error={errors.returnDate}
            >
              <DateField
                value={formState.returnDate}
                onChange={(d) => updateField("returnDate", d)}
                minDate={formState.date ?? today}
                maxDate={maxDate}
                placeholder={t("search.selectReturnDate")}
                invalid={!!errors.returnDate}
                testId="date-picker-return"
              />
            </Field>
          ) : (
            <Field label={t("search.passengers")}>
              <PassengerField
                value={formState.passengers}
                onChange={(v) => updateField("passengers", v)}
              />
            </Field>
          )}

          {/* Passengers — shown separately when round-trip takes the 5th slot */}
          {formState.tripType === "round-trip" && (
            <Field label={t("search.passengers")}>
              <PassengerField
                value={formState.passengers}
                onChange={(v) => updateField("passengers", v)}
              />
            </Field>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="group w-full h-14 md:h-16 bg-brand text-brand-ink font-semibold tracking-wide flex items-center justify-center gap-3 transition-[background] hover:bg-ink focus-visible-ring"
          data-testid="search-submit"
        >
          <Search className="h-5 w-5" aria-hidden="true" />
          <span className="text-[0.95rem] md:text-base uppercase tracking-[0.12em]">
            {t("search.searchButton")}
          </span>
        </button>
      </form>
    </div>
  );
}

/* ─── Sub-primitives ────────────────────────────────────────────── */

function TripTypePill({
  active,
  onClick,
  label,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "px-3 py-1.5 text-[0.7rem] uppercase tracking-[0.12em] border hairline-strong -ml-px first:ml-0 transition-colors focus-visible-ring",
        active
          ? "bg-ink text-stone-50 border-ink"
          : "bg-white text-ink-muted hover:text-ink",
      )}
    >
      {label}
    </button>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-5 md:px-6 py-4 md:py-5 flex flex-col justify-center min-w-0">
      <label className="text-eyebrow uppercase text-ink-subtle mb-2">
        {label}
      </label>
      {children}
      {error && (
        <p className="text-xs text-destructive mt-1.5" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function CityField({
  value,
  onChange,
  placeholder,
  cities,
  disabled,
  invalid,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  cities: City[];
  disabled?: boolean;
  invalid?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selected = cities.find((c) => c.id.toString() === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full flex items-center justify-between gap-2 text-left",
            "font-display text-[1.35rem] md:text-[1.55rem] leading-tight truncate",
            "focus-visible-ring disabled:opacity-50 disabled:cursor-not-allowed",
            selected ? "text-ink" : "text-ink-subtle",
            invalid && "text-destructive",
          )}
        >
          <span className="truncate">
            {selected ? cityLabel(selected) : placeholder}
          </span>
          <ChevronDown
            className="h-4 w-4 text-ink-subtle flex-shrink-0"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[16rem] p-0"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={t("search.searchCity", "Search city...")}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <CommandList>
            <CommandEmpty>
              {t("search.noCityFound", "No city found")}
            </CommandEmpty>
            <CommandGroup>
              {cities.map((city) => (
                <CommandItem
                  key={city.id}
                  // Unique per city: duplicate labels collide in cmdk (items
                  // sharing a value highlight/select together). The label
                  // stays searchable via `keywords` — cmdk's default filter
                  // scores against value + keywords.
                  value={cityOptionValue(city)}
                  keywords={cityOptionKeywords(city)}
                  onSelect={() => {
                    onChange(city.id.toString());
                    setOpen(false);
                  }}
                  className="text-base"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === city.id.toString()
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {cityLabel(city)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DateField({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder,
  invalid,
  testId,
}: {
  value: Date | undefined;
  onChange: (d: Date | undefined) => void;
  minDate: Date;
  maxDate: Date;
  placeholder: string;
  invalid?: boolean;
  testId?: string;
}) {
  // Controlled open state so picking a date CLOSES the popover. Left
  // uncontrolled, the calendar stayed open and (on phone widths) covered
  // the rest of the form including the Search button.
  const [open, setOpen] = useState(false);
  const dl = useDateLocale();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "w-full flex items-center justify-between gap-2 text-left tabular",
            "font-display text-[1.35rem] md:text-[1.4rem] leading-tight",
            value ? "text-ink" : "text-ink-subtle",
            invalid && "text-destructive",
          )}
        >
          <span className="truncate">
            {value ? format(value, dl.pattern("weekdayDate"), { locale: dl.locale }) : placeholder}
          </span>
          <Calendar
            className="h-4 w-4 text-ink-subtle flex-shrink-0"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarComponent
          mode="single"
          locale={dl.locale}
          selected={value}
          onSelect={(d) => {
            onChange(d);
            setOpen(false);
          }}
          disabled={(d) => d < minDate || d > maxDate}
        />
      </PopoverContent>
    </Popover>
  );
}

function PassengerField({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => value > 1 && onChange(value - 1)}
        disabled={value <= 1}
        className="h-8 w-8 flex items-center justify-center border hairline-strong text-ink-muted hover:text-ink hover:border-ink disabled:opacity-40 disabled:cursor-not-allowed focus-visible-ring"
        aria-label="Decrease passengers"
        data-testid="passenger-decrement"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span
        className="font-display text-[1.5rem] md:text-[1.6rem] text-ink tabular w-8 text-center"
        aria-live="polite"
        data-testid="passenger-count"
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => value < 10 && onChange(value + 1)}
        disabled={value >= 10}
        className="h-8 w-8 flex items-center justify-center border hairline-strong text-ink-muted hover:text-ink hover:border-ink disabled:opacity-40 disabled:cursor-not-allowed focus-visible-ring"
        aria-label="Increase passengers"
        data-testid="passenger-increment"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default BookingWidget;
